import { AnimatePresence, motion } from 'motion/react';
import { spring } from '../motion/presets';
import { cx } from '../internal/cx';

export type ThemeToggleProps = {
  /** Thème courant. */
  theme: 'dark' | 'light';
  /** Callback de bascule (le composant reste pur — pas d'accès au DOM/storage). */
  onToggle: () => void;
  className?: string;
};

/**
 * Bouton de bascule de thème : morph soleil/lune (rotation + scale),
 * libellé accessible dynamique. À brancher sur `useTheme()` côté app.
 */
export function ThemeToggle({ theme, onToggle, className }: ThemeToggleProps) {
  const isDark = theme === 'dark';
  return (
    <motion.button
      type="button"
      onClick={onToggle}
      aria-label={isDark ? 'Activer le thème clair' : 'Activer le thème sombre'}
      title={isDark ? 'Thème clair' : 'Thème sombre'}
      whileTap={{ scale: 0.9 }}
      transition={spring.snappy}
      className={cx(
        'relative inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-full',
        'border border-ink-600/60 bg-ink-700 text-ink-100 hover:shadow-glow-panda',
        'transition-shadow duration-fast ease-fun',
        className,
      )}
      data-theme-toggle={theme}
    >
      <AnimatePresence mode="wait" initial={false}>
        {isDark ? (
          <motion.svg
            key="moon"
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            aria-hidden="true"
            initial={{ rotate: -90, scale: 0.4, opacity: 0 }}
            animate={{ rotate: 0, scale: 1, opacity: 1 }}
            exit={{ rotate: 90, scale: 0.4, opacity: 0 }}
            transition={spring.soft}
          >
            <path
              d="M20 13.5A8 8 0 0 1 10.5 4 8 8 0 1 0 20 13.5Z"
              fill="currentColor"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinejoin="round"
            />
          </motion.svg>
        ) : (
          <motion.svg
            key="sun"
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            aria-hidden="true"
            initial={{ rotate: 90, scale: 0.4, opacity: 0 }}
            animate={{ rotate: 0, scale: 1, opacity: 1 }}
            exit={{ rotate: -90, scale: 0.4, opacity: 0 }}
            transition={spring.soft}
          >
            <circle cx="12" cy="12" r="4.5" fill="currentColor" />
            <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M12 2.5v2.4M12 19.1v2.4M2.5 12h2.4M19.1 12h2.4M5.2 5.2l1.7 1.7M17.1 17.1l1.7 1.7M18.8 5.2l-1.7 1.7M6.9 17.1l-1.7 1.7" />
            </g>
          </motion.svg>
        )}
      </AnimatePresence>
    </motion.button>
  );
}
